// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/truncatedJson.test.ts

// why: extracted out of SubagentToolStep.tsx (review #5399) — this is pure
// string/JSON reasoning with no React in it, and `SubagentToolStep.tsx` was
// already past the 400-line ceiling `.claude/rules/anti-ai-slop.md` sets. Here
// the cut-point cases (a cut inside `\uXXXX`, a cut after a trailing backslash,
// a cut mid-object) are reachable without rendering a component, so they are
// pinned directly instead of through a panel fixture.

export function stringField(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Re-close the containers that were still open at the last point `text` could
 * be cut cleanly (a comma, or a completed nested value), dropping whatever
 * token the cut landed inside. Returns null when nothing whole survives.
 *
 * `keepCutString` also closes the string literal the cut landed in, keeping
 * the characters that did arrive. A payload whose whole content is one long
 * string (`load_capability`'s `{"instructions": ...}`) has no earlier comma or
 * closer to fall back to, so dropping the cut token there drops everything.
 */
export function closeTruncatedJson(
  text: string,
  keepCutString = false,
): string | null {
  const open: string[] = [];
  let inString = false;
  let escaped = false;
  let cut = -1;
  let closers = "";
  // How much of the open string is whole characters: never mid-escape, so
  // closing here cannot leave a dangling `\` or a half-written `\uXXXX`.
  let stringCut = -1;
  // Hex digits still owed to an open `\uXXXX`. Without this the digits read as
  // ordinary characters and advanced `stringCut` between them, so a cut inside
  // one escape closed the string as `"...\u20"` -- invalid, the salvage threw
  // it away, and the row the salvage exists for went back to being empty.
  let unicodeLeft = 0;
  const mark = (index: number) => {
    if (open.length === 0) return;
    cut = index;
    closers = [...open].reverse().join("");
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inString) {
      if (unicodeLeft > 0) {
        unicodeLeft -= 1;
        // Only once the fourth digit has arrived is the escape a whole token.
        if (unicodeLeft === 0) stringCut = i + 1;
      } else if (escaped) {
        escaped = false;
        if (char === "u") unicodeLeft = 4;
      } else if (char === "\\") escaped = true;
      else if (char === '"') inString = false;
      else stringCut = i + 1;
      continue;
    }
    if (char === '"') {
      inString = true;
      stringCut = i + 1;
    } else if (char === "{") open.push("}");
    else if (char === "[") open.push("]");
    else if (char === "}" || char === "]") {
      open.pop();
      mark(i + 1);
    } else if (char === ",") mark(i);
  }

  if (keepCutString && inString && stringCut > cut) {
    return `${text.slice(0, stringCut)}"${[...open].reverse().join("")}`;
  }
  return cut > 0 ? `${text.slice(0, cut)}${closers}` : null;
}

export function parseJsonOrNull(text: string | null): unknown {
  if (text == null) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * why: christo caps every warhol tool_result summary at 500 chars
 * (`WARHOL_STEP_SUMMARY_CHARS`) and one `SubagentReport` already serializes to
 * ~250 of them, so a fan-out's reports JSON reaches this panel cut mid-object.
 * An all-or-nothing `JSON.parse` threw on that and dropped EVERY report,
 * including the ones that arrived whole -- the expanded rows showed the task
 * and nothing the subagent came back with. Salvage the complete prefix instead.
 */
export function parseTruncatedJson(
  text: string,
  keepCutString = false,
): unknown {
  const whole = parseJsonOrNull(text);
  if (whole != null) return whole;
  if (keepCutString) {
    const kept = parseJsonOrNull(closeTruncatedJson(text, true));
    if (kept != null) return kept;
  }
  return parseJsonOrNull(closeTruncatedJson(text));
}

/**
 * why: warhol's `load_capability` CALL carries only `{id}` -- the skill text
 * comes back on the RESULT, as `{"instructions": "..."}` (christo's
 * `warholAgentSteps`). This panel read `args.instructions`, which the wire
 * never sets, so every expanded "Load skill" row was an empty box. A skill
 * prompt is far longer than christo's 500-char summary cap, so that one string
 * always arrives cut -- keep the part that made it rather than nothing.
 */
export function capabilityInstructions(summary: string | undefined): string {
  const trimmed = (summary ?? "").trim();
  if (!trimmed.startsWith("{")) return "";
  const parsed = parseTruncatedJson(trimmed, true);
  if (parsed == null || typeof parsed !== "object") return "";
  return stringField((parsed as { instructions?: unknown }).instructions);
}
